<template>
  <section
    id="cart-overview"
    class="cart-overview"
  >
    <c-action
      class="cart-overview__close"
      color="secondary"
      @click="closeSidebar"
    >
      {{ $t('closeCart') }}
    </c-action>

    <h3 class="cart-overview__title">
      {{ $t('title') }}
    </h3>

    <transition-group name="transition-fade">
      <div
        v-if="$store.state.cartLoading"
        key="loading"
        class="cart-overview__loading"
      >
        <c-loader-spinner :size="1.5" />
      </div>

      <div
        v-else-if="$store.state.cart.total"
        key="articles"
      >
        <c-cart-timer class="cart-overview__timer" />

        <div
          v-if="$store.state.cart.tickets.total_articles"
          key="tickets"
          class="cart-overview__ticket"
        >
          <h4 class="cart-overview__ticket-title">
            {{ $t('admission') }}
          </h4>

          <p class="cart-overview__ticket-info">
            {{ date($store.state.cart.tickets.date) }}<br>
            {{ $t('arrivalSlot') }}: {{ gapTime }}
          </p>

          <div
            v-for="(article, typeId) in $store.state.cart.tickets.articles"
            :key="typeId"
            class="cart-overview__ticket-type"
          >
            <span class="cart-overview__ticket-count">{{ article.count }}</span>
            <span class="cart-overview__ticket-name">{{ articleTypes[$i18n.locale][typeId] }}</span>
            <span class="cart-overview__ticket-price">{{ price(article.count * article.discounted_price) }} €</span>
          </div>

          <p class="cart-overview__subtotal">
            {{ price($store.state.cart.tickets.subtotal) }} €
          </p>

          <button
            class="cart-overview__delete"
            @click="removeTickets"
          >
            <c-icon type="delete" />
            {{ $t('removeTickets') }}
          </button>

          <div
            v-if="$store.state.cart.tickets.fee_total"
            class="cart-overview__ticket-type cart-overview__ticket-type--fees"
          >
            <span
              class="cart-overview__ticket-name"
              v-html="$t('fee')"
            />
            <span class="cart-overview__ticket-price">
              {{ price($store.state.cart.tickets.fee_total) }} €
            </span>
          </div>
        </div>

        <div
          v-if="tours.articleAmount"
          key="tours"
          class="cart-overview__ticket"
        >
          <h4 class="cart-overview__ticket-title">
            {{ $t('tours') }}
          </h4>

          <p class="cart-overview__ticket-info">
            {{ date(tours.date) }}
          </p>

          <div
            v-for="(group, time) in tours.groupedArticles"
            :key="time"
          >
            <p class="cart-overview__ticket-group-info">
              <span class="text-quotes">{{ group.name }}</span>, {{ $t('tourStart') }}: {{ time }}
            </p>

            <div
              v-for="article in group.articles"
              :key="article.id"
              class="cart-overview__ticket-type"
            >
              <span class="cart-overview__ticket-count">{{ article.count }}</span>
              <span class="cart-overview__ticket-name">{{ articleTypes[$i18n.locale][article.id] }}</span>
              <span class="cart-overview__ticket-price">{{ price(article.subtotal) }} €</span>
            </div>
          </div>

          <p class="cart-overview__subtotal">
            {{ price(tours.subtotal) }} €
          </p>

          <button
            class="cart-overview__delete"
            @click="removeTours"
          >
            <c-icon type="delete" />
            {{ $t('removeTours') }}
          </button>

          <div
            v-if="tours.fee"
            class="cart-overview__ticket-type cart-overview__ticket-type--fees"
          >
            <span
              class="cart-overview__ticket-name"
              v-html="$t('fee')"
            />
            <span class="cart-overview__ticket-price">
              {{ price(tours.fee) }} €
            </span>
          </div>
        </div>

        <div
          v-if="$store.state.cart.events.total_articles"
          key="events"
          class="cart-overview__ticket"
        >
          <h4 class="cart-overview__ticket-title">
            {{ $t('specialEvents') }}
          </h4>

          <p class="cart-overview__ticket-info">
            {{ date($store.state.cart.events.date) }}<br>
            {{ $t('arrivalEvent') }}: {{ $store.state.selectedEventTimes.from }} - {{ $store.state.selectedEventTimes.until }}
          </p>

          <div
            v-for="(article, typeId) in $store.state.cart.events.articles"
            :key="typeId"
            class="cart-overview__ticket-type"
          >
            <span class="cart-overview__ticket-count">{{ article.count }}</span>
            <span class="cart-overview__ticket-name">{{ articleTypes[$i18n.locale][typeId] }}</span>
            <span class="cart-overview__ticket-price">{{ price(article.count * article.price) }} €</span>
          </div>

          <p class="cart-overview__subtotal">
            {{ price($store.state.cart.events.subtotal) }} €
          </p>

          <button
            class="cart-overview__delete"
            @click="removeEvents"
          >
            <c-icon type="delete" />
            {{ $t('removeSpecialEvents') }}
          </button>

          <div
            v-if="$store.state.cart.events.fee_total"
            class="cart-overview__ticket-type cart-overview__ticket-type--fees"
          >
            <span
              class="cart-overview__ticket-name"
              v-html="$t('fee')"
            />
            <span class="cart-overview__ticket-price">
              {{ price($store.state.cart.events.fee_total) }} €
            </span>
          </div>
        </div>

        <div
          v-if="$store.state.cart.vouchers.total_articles"
          key="vouchers"
          class="cart-overview__ticket"
        >
          <h4 class="cart-overview__ticket-title">
            {{ $t('vouchers') }}
          </h4>

          <div
            v-for="(article, voucherId) in $store.state.cart.vouchers.articles"
            :key="voucherId"
            class="cart-overview__ticket-type"
          >
            <span class="cart-overview__ticket-count">{{ article.count }}</span>
            <span class="cart-overview__ticket-name">{{ voucherTitle(voucherId) }}</span>
            <span class="cart-overview__ticket-price">{{ price(article.price * article.count) }} €</span>
          </div>

          <p class="cart-overview__subtotal">
            {{ price($store.state.cart.vouchers.total) }} €
          </p>

          <button
            class="cart-overview__delete"
            @click="removeVouchers"
          >
            <c-icon type="delete" />
            {{ $t('removeVouchers') }}
          </button>
        </div>

        <p class="cart-overview__total">
          <span>{{ $t('total') }}:</span>
          <strong>{{ price($store.state.cart.total) }} €</strong>
        </p>

        <c-action-link
          v-if="cartValidation.valid"
          color="primary"
          :router-link="{ name: 'booking-overview-' + $i18n.locale }"
        >
          {{ $t('continue') }}
          <c-icon
            type="chevron-right"
            :size="0.75"
          />
        </c-action-link>

        <p
          v-else
          class="cart-overview__error"
        >
          {{ $t(cartValidation.error, cartValidation.errorArgs) }}
        </p>

        <c-action-link
          v-if="showTourSelectButton"
          color="primary"
          :router-link="{ name: 'booking-tours-' + $i18n.locale }"
        >
          {{ $t('selectTours') }}
          <c-icon
            type="chevron-right"
            :size="0.75"
          />
        </c-action-link>

        <p
          v-if="$store.state.cart.tickets.total_articles"
          class="cart-overview__note"
        >
          {{ $t('note') }}
          <button
            class="link"
            @click="scrollToVisitorInfo"
          >
            {{ $t('changeNow') }}
          </button>
        </p>
      </div>

      <p
        v-else
        key="empty"
        class="cart-overview__empty"
      >
        {{ $t('empty') }}
      </p>
    </transition-group>
  </section>
</template>

<script>
import jump from 'jump.js'

import CAction from './action.vue'
import CActionLink from './actionLink.vue'
import CCartTimer from './CCartTimer.vue'
import CIcon from './icon.vue'
import CLoaderSpinner from './loaderSpinner.vue'

import cartMixin from '../mixins/cart.js'
import cartValidationMixin from '../mixins/cartValidation.js'

import humanDate from '../functions/humanDate.js'
import priceHelper from '../functions/priceHelper.js'
import timeFromGap from '../functions/timeFromGap.js'

import { BREAKPOINT_SIDEBAR } from '../settings.js'

export default {
  name: 'CCartOverviewTickets',

  components: {
    CAction,
    CActionLink,
    CCartTimer,
    CIcon,
    CLoaderSpinner
  },

  mixins: [cartMixin, cartValidationMixin],

  computed: {
    specialEvent () {
      return this.$store.state.tickets.specialEvent
    },

    gapTime () {
      if (this.reservation) {
        return timeFromGap(this.$store.state.cart.reservations.gap)
      }

      return timeFromGap(this.$store.state.cart.tickets.gap)
    },

    showTourSelectButton () {
      if (this.$route.name.startsWith('booking-tours')) {
        return false
      }

      if (this.cartValidation.error !== 'cartError.tours') {
        return false
      }

      return true
    }
  },

  created () {
    const dontResetSelection = !isNaN(parseInt(this.$route.params.people))
    this.$store.dispatch('getCart', { dontResetSelection })

    BREAKPOINT_SIDEBAR.addListener(event => {
      if (event.matches) {
        this.closeSidebar()
      }
    })
  },

  destroyed () {
    this.closeSidebar()
  },

  methods: {
    scrollToVisitorInfo () {
      jump('#visitor-info', { duration: 500 })
    },

    voucherTitle: function (voucherId) {
      const vouchers = this.$store.state.vouchers

      for (const voucherGroup in vouchers) {
        for (const voucherIndex in vouchers[voucherGroup]) {
          if (vouchers[voucherGroup][voucherIndex].id === parseInt(voucherId)) {
            return this.$i18n.locale === 'de'
              ? vouchers[voucherGroup][voucherIndex].text
              : vouchers[voucherGroup][voucherIndex].text_en
          }
        }
      }

      return ''
    },

    removeVouchers () {
      this.$store.dispatch('removeFromCart', { endpoint: 'vouchers' })
    },

    date (date) {
      const prettyDate = humanDate(date, this.$i18n.locale)
      return `${prettyDate.weekday.short}, ${prettyDate.date.long}`
    },

    price (price) {
      return priceHelper.format((price / 100), this.$i18n.locale)
    },

    removeTickets () {
      this.$store.dispatch('removeFromCart', { endpoint: 'tickets', dontResetSelection: true })
    },

    removeTours () {
      this.$store.dispatch('removeFromCart', { endpoint: 'tours' })
    },

    removeEvents () {
      this.$store.dispatch('removeFromCart', { endpoint: 'events' })
    },

    closeSidebar () {
      this.$store.commit('showContainerSidebar', false)
    }
  }
}
</script>

<i18n locale="en">
{
  "title": "Overview",
  "closeCart": "Close cart",
  "admission": "Tickets",
  "tours": "Experiences",
  "specialEvents": "Special events",
  "vouchers": "Vouchers",
  "arrivalSlot": "Time slot of arrival",
  "arrivalEvent": "Event time",
  "time": "Time",
  "tourStart": "Start",
  "total": "Total",
  "fee": "plus system fees 0.50&nbsp;€ per ticket",
  "removeTickets": "Delete tickets",
  "removeTours": "Delete experiences",
  "removeSpecialEvents": "Delete special events",
  "removeVouchers": "Delete vouchers",
  "continue": "Book now",
  "note": "To change the number / type of tickets simply enter a new number of people or select a new type of ticket in Visitor Information. In order to change the date or time frame just select a different time slot.",
  "changeNow": "Change now!",
  "empty": "Please select the desired number of tickets, the ticket type, and the time slot of arrival.",
  "selectTours": "Experiences"
}
</i18n>

<i18n locale="de">
{
  "title": "Preisübersicht",
  "closeCart": "Warenkorb schließen",
  "admission": "Eintrittskarten",
  "tours": "Erlebnisse",
  "specialEvents": "Sonderveranstaltungen",
  "vouchers": "Gutscheine",
  "arrivalSlot": "Einlasszeit",
  "arrivalEvent": "Veranstaltungszeit",
  "time": "Uhrzeit",
  "tourStart": "Begin",
  "total": "Gesamt",
  "fee": "zzgl. Systemgebühren 0,50&nbsp;€ je Ticket",
  "removeTickets": "Tickets löschen",
  "removeTours": "Erlebnisse löschen",
  "removeSpecialEvents": "Sonderveranstaltungen löschen",
  "removeVouchers": "Gutscheine löschen",
  "continue": "Jetzt buchen",
  "note": "Um die Personenanzahl-/ oder -auswahl zu ändern, müssen Sie lediglich bei den Besucherdaten eine neue Anzahl angeben oder eine neue Auswahl treffen. Um das Datum oder Zeitfenster zu verändern, müssen Sie lediglich in der Ankunfts­zeitraum­auswahl ein neues Zeitfenster anklicken.",
  "changeNow": "Jetzt ändern!",
  "empty": "Bitte wählen Sie die gewünschte Anzahl an Tickets, die Ticketart und die Ankunftszeit.",
  "selectTours": "Erlebnisse"
}
</i18n>

<style lang="scss">
.cart-overview__loading {
  text-align: center;
  padding: 1.25em;
}
</style>
